import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { CheckCircle2, AlertCircle } from "lucide-react";
import { Card, CardContent } from "@/components/ui/Card.tsx";
import { Button } from "@/components/ui/Button.tsx";
import { Spinner } from "@/components/ui/Spinner.tsx";
import { apiPost } from "@/api/client.ts";

export function TeamAcceptPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token");
  const [attempted, setAttempted] = useState(false);

  const accept = useMutation({
    mutationFn: () => apiPost<{ ok: boolean; message: string }>("/team/accept", { token }),
  });

  useEffect(() => {
    if (token && !attempted) {
      setAttempted(true);
      accept.mutate();
    }
  }, [token, attempted]);

  if (!token) {
    return (
      <div className="mx-auto max-w-md p-8">
        <Card>
          <CardContent className="flex flex-col items-center gap-4 py-8">
            <AlertCircle className="h-8 w-8 text-danger" />
            <p className="text-fg font-medium">Invalid invite link</p>
            <p className="text-sm text-fgMuted">This link is missing the invite token.</p>
            <Button onClick={() => navigate("/dashboard")}>Go to dashboard</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (accept.isPending) {
    return (
      <div className="mx-auto max-w-md p-8">
        <Card>
          <CardContent className="flex flex-col items-center gap-4 py-8">
            <Spinner size="lg" />
            <p className="text-fg font-medium">Accepting invite...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (accept.isError) {
    const err = accept.error as { message?: string };
    return (
      <div className="mx-auto max-w-md p-8">
        <Card>
          <CardContent className="flex flex-col items-center gap-4 py-8">
            <AlertCircle className="h-8 w-8 text-danger" />
            <p className="text-fg font-medium">Could not accept invite</p>
            <p className="text-sm text-fgMuted text-center">{err.message ?? "Something went wrong."}</p>
            <Button onClick={() => navigate("/dashboard")}>Go to dashboard</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-md p-8">
      <Card>
        <CardContent className="flex flex-col items-center gap-4 py-8">
          <CheckCircle2 className="h-8 w-8 text-success" />
          <p className="text-fg font-medium">You've joined the team!</p>
          <p className="text-sm text-fgMuted text-center">
            You now share access to the team's videos, credits, and channels.
          </p>
          <Button onClick={() => navigate("/dashboard")}>Go to dashboard</Button>
        </CardContent>
      </Card>
    </div>
  );
}
